import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { useState } from "react";

import Loader from "./Loader";

import supportTicketService from "../services/supportTicketService";
import useSupportTicket from "../hooks/useSupportTicket";
import { formatDateToSydneyTimeZone } from "../utils/dateUtils";

const Container = ({ children, onCloseClicked }) => (
  <div className="bg-white rounded-lg border border-ase-blue w-[100%] md:w-[700px] pb-1">
    <div className="flex items-center justify-between text-lg border-b bg-ase-blue text-white p-4 font-bold rounded-t-md">
      <div className="flex items-center gap-2">
        Ticket
      </div>

      <p
        onClick={onCloseClicked}
        className="font-bold cursor-pointer text-2xl"
      >
        X
      </p>
    </div>

    {children}
  </div>
)

const TicketView = ({ ticketId, onUpdateClicked, onDeleteClicked, onCloseClicked }) => {
  const { user } = useContext(AuthContext);

  const [message, setMessage] = useState('');

  const chatEl = useRef(null);
  const chatInputEl = useRef(null);

  const { data, isLoading, mutate } = useSupportTicket(
    { ticketId },
    { refreshInterval: 5 * 1000 }
  );

  const ticket = data?.supportTicket;

  const handleMessageSent = async () => {
    const { sentMessage } = await supportTicketService.addMessageToTicket({
      ticketId: ticket._id,
      user: user._id,
      message
    });

    ticket.messages.push({
      ...sentMessage,
      user
    });

    mutate();

    setMessage('');
  };

  const handleMessageFormSubmit = (e) => {
    e.preventDefault();

    handleMessageSent();
  }

  useEffect(() => {
    if (!chatEl.current) return;

    chatEl.current.scrollTop = chatEl.current.scrollHeight;
    chatInputEl.current.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages.length]);

  if (isLoading) return (
    <Container onCloseClicked={onCloseClicked}>
      <div className="flex items-center justify-center p-6">
        <Loader />
      </div>
    </Container>
  );

  return (
    <Container onCloseClicked={onCloseClicked}>
      <div className="flex flex-col gap-2 border-b p-4">
        <div className="flex flex-col gap-2">
          <p><b>Title</b>: {ticket.title}</p>
        </div>

        <div className="flex flex-col gap-2">
          <p><b>Description</b>: {ticket.description}</p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-1">
          <p><b>Created by:</b> {ticket.createdBy.fullName}</p>
          <p><b>Date: </b> {formatDateToSydneyTimeZone(ticket.createdAt)}</p>
        </div>

        <div className='flex items-center gap-2 mt-1'>
          <button
            onClick={onUpdateClicked}
            className='ase-button-primary text-sm'
          >
            Update
          </button>

          <button
            onClick={onDeleteClicked}
            className='ase-button-danger text-sm'
          >
            Delete
          </button>
        </div>
      </div>

      <div className="flex flex-col">
        <p className="px-4 bg-ase-blue text-white font-semibold py-3">Chat</p>

        {ticket?.messages?.length > 0 && (
          <div ref={chatEl} className="flex flex-col gap-2 p-4 max-h-[300px] overflow-y-auto">
            {ticket.messages.map((message, index) => (
              <div
                key={index}
                className={`flex flex-col gap-1 p-4 rounded-md text-white ${message.user._id === user._id ? 'self-end brightness-90 bg-ase-blue' : 'self-start bg-ase-blue'}`}>
                <p className="text-sm font-semibold">{message.user._id === user._id ? 'You' : message.user.fullName}</p>
                <p className='text-sm'>{message.message}</p>
                <p className='text-sm'>{formatDateToSydneyTimeZone(message.date)}</p>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleMessageFormSubmit}>
          <div className="flex items-center gap-2 p-4">
            <input
              type="text"
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              ref={chatInputEl}
              className="flex-grow border border-gray-300 rounded-md p-2"
            />

            <button
              type="submit"
              className="bg-ase-blue text-white font-medium py-2 px-4 rounded-md">
              Send
            </button>
          </div>
        </form>
      </div>
    </Container>
  )
}

export default TicketView;
